import { BorderBox, iTxt, MediumButton } from "@duosoftbg/nacid-components";
import { getI18n, useTranslation } from "react-i18next";
import { Grid, IconButton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { Dot } from "../../components/common/Dot";
import { EqfStatus } from "../../../types/eqfStatus";
import { useEffect, useState } from "react";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { faRectangleList } from "@fortawesome/free-regular-svg-icons";
import { CountryDetails } from "../../../types/QFDataTypes";
import DialogQF from "./DialogQF";
import "./stylesTableQF.css";

export const EQF_STATUS_COLORS = {
  [EqfStatus.full]: "#38a169",
  [EqfStatus.partial]: "#dd6b20",
  [EqfStatus.none]: "#e53e3e",
};

export const CountryCardsPage = ({ results }: { results: CountryDetails[] }) => {
  const [page, setPage] = useState(0);
  const [pagesData, setPagesData] = useState([]);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const pageSize = 8;
  const totalPages = results.length === 0 ? 0 : Math.ceil(results.length / pageSize);

  useEffect(() => {
    const pages = Array.from({ length: totalPages }, (_, i) => results.slice(i * pageSize, (i + 1) * pageSize));
    setPagesData(pages);
  }, [results, totalPages]);

  const handlePrev = () => {
    setPage((p) => Math.max(p - 1, 0));
    setTimeout(() => {}, 100);
    setDirection("left");
  };
  const handleNext = () => {
    setPage((p) => Math.min(p + 1, totalPages - 1));
    setTimeout(() => {}, 100);
    setDirection("right");
  };

  const handleFirst = () => {
    setPage(0);
    setTimeout(() => {}, 100);
    setDirection("left");
  };

  const handleLast = () => {
    setPage(totalPages - 1);
    setTimeout(() => {}, 100);
    setDirection("right");
  };

  return (
    <div style={{ overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <IconButton onClick={handleFirst} disabled={page === 0 || totalPages <= 1}>
          <FirstPageIcon sx={{ color: page === 0 ? "#656D78" : "#1D5083" }} />
        </IconButton>

        <IconButton onClick={handlePrev} disabled={page === 0 || totalPages <= 1}>
          <ArrowBackIos sx={{ color: page === 0 ? "#656D78" : "#1D5083" }} />
        </IconButton>
        <span style={{ marginTop: "7px", fontSize: "14px" }}>
          Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
        </span>
        <IconButton onClick={handleNext} disabled={page === totalPages - 1 || totalPages <= 1}>
          <ArrowForwardIos sx={{ color: page === totalPages - 1 || totalPages <= 1 ? "#656D78" : "#1D5083" }} />
        </IconButton>
        <IconButton onClick={handleLast} disabled={page === totalPages - 1 || totalPages <= 1}>
          <LastPageIcon sx={{ color: page === totalPages - 1 || totalPages <= 1 ? "#656D78" : "#1D5083" }} />
        </IconButton>
      </div>
      <Grid container sx={{ mt: 2 }} spacing={2} padding={5}>
        {pagesData.length > 0 &&
          pagesData[page].map((result: CountryDetails) => (
            <Grid item key={result.country.id} sm={6} md={4} lg={3} style={{ display: "flex" }}>
              <CountryDetailsCard countryDetails={result} direction={direction} />
            </Grid>
          ))}
      </Grid>
    </div>
  );
};

const CountryDetailsCard = ({ countryDetails, direction }: { countryDetails: CountryDetails; direction: string }) => {
  const { t } = useTranslation();
  const [countryId, setCountryId] = useState<string | null>(null);
  const handleCloseDialog = () => {
    setCountryId(null);
  };

  return (
    <>
      <BorderBox
        className={direction === "left" ? "card-slide-left" : "card-slide-right"}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          wordBreak: "break-word",
          borderRadius: 3,
          borderColor: "#656D78",
        }}
        style={{ height: "auto", paddingBottom: "2px" }}
      >
        <Grid container spacing={3} sx={{ p: 4, flexGrow: 1, alignContent: "start" }}>
          <Grid item xs={3}>
            <Box
              component="img"
              sx={{ width: 48, height: 36, borderRadius: 1, border: "1px solid lightgray" }}
              src={countryDetails.flagUrl}
            />
          </Grid>
          <Grid item xs={9}>
            <Typography fontWeight={800} fontSize={16} mb={1} noWrap={true}>
              {iTxt(getI18n().language, countryDetails.country.name, countryDetails.country.nameEn)}
            </Typography>
            <Typography fontWeight={600} fontSize={16}>
              ({countryDetails.country.nativeName})
            </Typography>
          </Grid>

          <Grid item xs={12} fontSize={14}>
            {iTxt(getI18n().language, countryDetails.systemSummary, countryDetails.systemSummaryEn)}
          </Grid>
          <Grid item xs={12} fontSize={14}>
            <Dot color={EQF_STATUS_COLORS[countryDetails.eqfStatus]} />{" "}
            <span style={{ fontWeight: 800 }}>{t(`l.eqfStatus.${countryDetails.eqfStatus}`)}</span>
          </Grid>
          <Grid item xs={12} fontSize={14}>
            {iTxt(getI18n().language, countryDetails.eqfDescription, countryDetails.eqfDescriptionEn)}
          </Grid>
        </Grid>
        <Box sx={{ borderTop: "1px solid #e2e8f0" }}>
          <Grid container spacing={3} sx={{ p: 4 }}>
            <Grid item xs={10}>
              <MediumButton
                startIcon={
                  <div style={{ position: "relative", width: "1em", height: "1em" }}>
                    <FontAwesomeIcon icon={faRectangleList} style={{ position: "absolute", top: 0, left: 0 }} />
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      style={{
                        position: "absolute",
                        bottom: "-1px",
                        right: "-4.5px",
                        fontSize: "0.7em",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>
                }
                onClick={() => {
                  setCountryId(countryDetails.country.id);
                }}
              >
                {t("l.btn.expandDetails")}
              </MediumButton>
            </Grid>
          </Grid>
        </Box>
      </BorderBox>
      {countryId && <DialogQF onClose={() => handleCloseDialog()} countryId={countryId} />}
    </>
  );
};
